import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const CalculatorContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
  font-family: "Helvetica";
  width: 100%;
  max-width: 850px;
  margin: 60px auto 0;

  @media (max-width: 768px) {
    padding: 2rem;
    width: 85%;
  }
`;

const CalculatorForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: #e1ad21;
  margin-bottom: 0.5rem;
  font-size: 14px;
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 44px;
  background-color: #0a192f;
  color: #ffffff;
  border: 1px solid #e1ad21;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  max-width: 130px;
  height: 35px;
  background-color: #0a192f;
  color: #ffffff;
  border: 1px solid #e1ad21;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 16px;

  /* Remove arrows for Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove arrows for Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const CalculatedPrice = styled.div`
  margin-top: 1rem;
  margin-left: 1.5rem;
  font-size: 1rem;
  color: #e1ad21;
  text-align: center;
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-left: 2.5rem;
  align-items: center;
`;

const SqmLabel = styled.span`
  color: #ffffff;
  margin-left: 0.5rem;
`;

function InteractivePricingCalculator() {
  const [serviceType, setServiceType] = useState('rendering');
  const [finishType, setFinishType] = useState('Unfurnished');
  const [resolution, setResolution] = useState('1080p');
  const [size, setSize] = useState('');
  const [price, setPrice] = useState(0);
  const [isCustomizable, setIsCustomizable] = useState(false);

  useEffect(() => {
    calculatePrice();
  }, [serviceType, finishType, resolution, size, isCustomizable]);

  const calculatePrice = () => {
    if (size === '' || isNaN(size) || Number(size) < 0) {
      setPrice(0);
      return;
    }

    const sizeNum = Number(size);

    if (sizeNum > 150) {
      setPrice('Custom Quote');
      return;
    }

    if (serviceType === 'rendering') {
      let basePrice = 0;

      // Base rendering rates (unchanged from your code)
      if (sizeNum <= 50) {
        basePrice = sizeNum * 6;
      } else if (sizeNum <= 100) {
        basePrice = sizeNum * 8;
      } else {
        basePrice = sizeNum * 10;
      }

      // NEW: Tiered Designed pricing per your exact specs
      if (finishType === 'Furnished') {
        const furnishedSurcharge = Math.min(sizeNum * 8, 1200);
        basePrice += furnishedSurcharge;
      } else if (finishType === 'Designed') {
        
        let designedFee;
        if (sizeNum <= 50) {
          designedFee = sizeNum * 20; 
        } else if (sizeNum <= 100) {
          designedFee = sizeNum * 28; 
        } else {
          designedFee = sizeNum * 30 ; 
        }
        basePrice += designedFee;
      }

      if (resolution === '4k') {
        basePrice += 500;
      }

      setPrice(Math.round(basePrice));

    } else if (serviceType === 'vr') {
      let basePrice = 0;

      // Base VR rates (unchanged from your code)
      if (sizeNum <= 50) {
        basePrice = sizeNum * 8;
      } else if (sizeNum <= 100) {
        basePrice = sizeNum * 10;
      } else {
        basePrice = sizeNum * 12;
      }

      // NEW: Tiered Designed pricing per your exact specs
      if (finishType === 'Furnished') {
        const furnishedSurcharge = Math.min(sizeNum * 8, 1200);
        basePrice += furnishedSurcharge;
      } else if (finishType === 'Designed') {
        
        let designedFee;
        if (sizeNum <= 50) {
          designedFee = sizeNum * 20; 
        } else if (sizeNum <= 100) {
          designedFee = sizeNum * 28; 
        } else {
          designedFee = sizeNum * 30; 
        }
        basePrice += designedFee;
      }

      if (isCustomizable) {
        basePrice += 750;
      }

      setPrice(Math.round(basePrice));
    }
  };

  return (
    <CalculatorContainer>
      <h3 style={{ textAlign: 'center', color: '#e1ad21' }}>Pricing Calculator</h3>
      <CalculatorForm>
        <FormGroup>
          <Label>Service Type</Label>
          <Select value={serviceType} onChange={(e) => {
            setServiceType(e.target.value);
            setFinishType('Unfurnished');
          }}>
            <option value="rendering">Rendering</option>
            <option value="vr">VR Experience</option>
          </Select>
        </FormGroup>

        {serviceType === 'rendering' && (
          <>
            <FormGroup>
              <Label>Finish Type</Label>
              <Select value={finishType} onChange={(e) => setFinishType(e.target.value)}>
                <option value="Unfurnished">Unfurnished</option>
                <option value="Furnished">Furnished (+€8/sqm, max €1200)</option>
                <option value="Designed">Designed by NueDesign (€20-26.5/sqm)</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Resolution</Label>
              <Select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                <option value="1080p">1080p</option>
                <option value="4k">4K (+€500)</option>
              </Select>
            </FormGroup>
          </>
        )}

        {serviceType === 'vr' && (
          <>
            <FormGroup>
              <Label>Customization</Label>
              <Select value={isCustomizable.toString()} onChange={(e) => setIsCustomizable(e.target.value === 'true')}>
                <option value="false">No Customization</option>
                <option value="true">Customizable (+€750)</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Finish Type</Label>
              <Select value={finishType} onChange={(e) => setFinishType(e.target.value)}>
                <option value="Unfurnished">Unfurnished</option>
                <option value="Furnished">Furnished (+€8/sqm, max €1200)</option>
                <option value="Designed">Designed by NueDesign (€20-26.5/sqm)</option>
              </Select>
            </FormGroup>
          </>
        )}

        <FormGroup>
          <Label>Size</Label>
          <InputWrapper>
            <Input 
              type="number" 
              value={size} 
              onChange={(e) => setSize(e.target.value)} 
              min="0" 
              placeholder="Enter size"
            />
            <SqmLabel>sqm</SqmLabel>
          </InputWrapper>
        </FormGroup>
      </CalculatorForm>
      <CalculatedPrice>
        {price === 'Custom Quote' 
          ? 'Custom Quote'
          : `Estimated Price: €${price}`
        }
      </CalculatedPrice>
    </CalculatorContainer>
  );
}

export default InteractivePricingCalculator;
